import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '../../components/Icons';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastNotificationProps {
  toast: ToastMessage | null;
  onClose: () => void;
  duration?: number;
}

export const ToastNotification: React.FC<ToastNotificationProps> = ({
  toast,
  onClose,
  duration = 3000,
}) => {
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => {
      onClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [toast, onClose, duration]);

  if (!toast) return null;

  const bgStyles = {
    success: 'bg-emerald-600 text-white border-emerald-700 shadow-emerald-900/20',
    error: 'bg-rose-600 text-white border-rose-700 shadow-rose-900/20',
    info: 'bg-sky-600 text-white border-sky-700 shadow-sky-900/20',
  }[toast.type];

  const IconComponent = toast.type === 'success'
    ? CheckCircleIcon
    : toast.type === 'error'
    ? ExclamationTriangleIcon
    : InformationCircleIcon;

  const content = (
    <div
      className="fixed top-6 left-1/2 -translate-x-1/2 z-[99999] w-[90%] max-w-md pointer-events-auto transition-all duration-300 transform animate-in fade-in slide-in-from-top-4"
      role="alert"
    >
      <div className={`flex items-start p-4 rounded-xl border shadow-2xl backdrop-blur-sm ${bgStyles}`}>
        <div className="flex-shrink-0 pt-0.5">
          <IconComponent className="h-5 w-5 text-white" />
        </div>
        <div className="ml-3 flex-1 text-sm font-semibold pr-2 leading-snug text-white">
          {toast.message}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 ml-auto inline-flex text-white/80 hover:text-white focus:outline-none p-1 rounded-md transition-colors"
          aria-label="Cerrar"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(content, document.body)
    : content;
};
